import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useSPMPrefetchTokenRegistry } from 'contexts/SPMPrefetchTokenContext';
import { MAX_PREFETCH_ATTEMPTS, useTQPrefetchTokenRegistry } from 'contexts/TQPrefetchTokenContext';
import { useDebouncedToast } from 'hooks/useDebouncedToast';
import { AsyncPollerService } from 'services/asyncPoller';
import {
  TQPrefetchDataFieldValuesResponse,
  usePrefetchDataFieldValuesMutation,
} from 'services/dataFieldValuesPrefetch';
import { TQPrefetchDataFieldValuesParams } from 'types/dataFieldValue';
import { APIError } from 'utils/errors';
import { useApprovalContext } from 'contexts/ApprovalContext';

type UsePrefetchDataFieldValuesParams = {
  isPrefetchActive?: boolean;
  params: TQPrefetchDataFieldValuesParams;
  shouldNormalizeParams?: boolean;
};

type UsePrefetchDataFieldValuesReturn = {
  prefetchMutation: UseMutationResult<
    TQPrefetchDataFieldValuesResponse,
    APIError,
    TQPrefetchDataFieldValuesParams,
    unknown
  >;
  isPrefetching: boolean;
};

/**
 * Hook to prefetch TQ data field values and manage prefetch tokens.
 * Automatically triggers prefetch when token doesn't exist and handles retries.
 */
export const usePrefetchDataFieldValues = ({
  params,
  isPrefetchActive = true,
  shouldNormalizeParams = true,
}: UsePrefetchDataFieldValuesParams): UsePrefetchDataFieldValuesReturn => {
  const { modelId, dataObjectSchemaId, territoryId, sortColumnUuid, sortAscending, page, limit, columnFilter } = params;
  const { activeChangeGroupId, isApprovalMode, proposedChangeGroupId } = useApprovalContext();
  const queryClient = useQueryClient();
  const { showDebouncedErrorToast, clearDebounceKey } = useDebouncedToast();
  const {
    getTokenFromParams,
    registerToken,
    clearToken,
    generateTokenKey,
    getPrefetchAttempts,
    incrementPrefetchAttempts,
    clearAllTokensForModel,
  } = useTQPrefetchTokenRegistry();
  const { clearRegistry: clearSPMPrefetchTokenRegistry } = useSPMPrefetchTokenRegistry();
  const [isFetchingNewToken, setIsFetchingNewToken] = useState(false);
  const { t } = useTranslation();

  // Generate the token key for this query combination
  const tokenKey = generateTokenKey(params, shouldNormalizeParams);
  const existingTokenInRegistry = getTokenFromParams(params, shouldNormalizeParams);
  // Serialize columnFilter for tracking changes
  const columnFilterKey = columnFilter ? JSON.stringify(columnFilter) : undefined;

  // The registry key does not encode the change group, so every change-group variant of a schema
  // competes for one slot. A job launched for a superseded change group must therefore never
  // register: its payload is a different dataset (base data vs merged, or another Repeat-By
  // branch), and registering it both serves the wrong rows and suppresses the corrective prefetch,
  // since the effect below skips while `existingTokenInRegistry` is set.
  const activeChangeGroupIdRef = useRef(activeChangeGroupId);
  activeChangeGroupIdRef.current = activeChangeGroupId;

  const { mutation: prefetchMutation } = usePrefetchDataFieldValuesMutation({
    onSuccess: async (data, variables) => {
      const launchChangeGroupId = variables.proposedChangeGroupId;
      if (launchChangeGroupId !== activeChangeGroupIdRef.current) {
        return;
      }

      setIsFetchingNewToken(true);

      try {
        // Wait for backend job to complete fetching the new token
        await AsyncPollerService.waitForJobAggregationsCompletion(
          `prefetch-data-field-values-${data.aggregation_id}`,
          data.aggregation_id,
          true,
          undefined,
          undefined,
          new URLSearchParams({ hidden: 'true' }),
        );

        // Re-check after the poll: the reviewed change group can be superseded while the job runs
        // (toggling proposed changes, switching Repeat-By branch), and this poll takes seconds.
        if (launchChangeGroupId !== activeChangeGroupIdRef.current) {
          return;
        }

        // Register token in the registry once prefetch completes
        if (data.prefetch_token) {
          registerToken(tokenKey, data.prefetch_token);
        }
      } catch (error) {
        // Clear the failed token attempt so we can retry
        if (tokenKey) {
          clearToken(tokenKey);
        }
        const errorMessage =
          error instanceof Error ? error.message : t('TQDataGrid.data_field_values.prefetch_failed_generic');
        showDebouncedErrorToast(errorMessage, undefined, `${errorMessage}-${tokenKey}`);
      } finally {
        setIsFetchingNewToken(false);
      }
    },
    onError: () => {
      setIsFetchingNewToken(false);
    },
  });

  // Trigger prefetch when token doesn't exist and max retries not exceeded
  useEffect(() => {
    // Skip if preconditions not met.
    //
    // In approval mode the change group only becomes known once the approval query resolves, so an
    // unguarded prefetch here fires against base data first and its token lands in the registry
    // slot the merged payload needs. Waiting mirrors the guard usePrefetchRecords already applies.
    if (
      !modelId ||
      !dataObjectSchemaId ||
      existingTokenInRegistry ||
      prefetchMutation.isPending ||
      isFetchingNewToken ||
      !isPrefetchActive ||
      (isApprovalMode && !proposedChangeGroupId)
    ) {
      return;
    }

    const attempts = getPrefetchAttempts(tokenKey);
    // Check retry limit
    // TODO: stop attempting to retry if the snapshot log is not found. We dont have a good way of passing around
    // whether the snapshot log is found or not.
    if (attempts >= MAX_PREFETCH_ATTEMPTS) {
      return;
    }
    // Increment counter before attempting prefetch
    incrementPrefetchAttempts(tokenKey);
    prefetchMutation.mutate({
      ...params,
      proposedChangeGroupId: activeChangeGroupId,
    });
  }, [
    activeChangeGroupId,
    isApprovalMode,
    proposedChangeGroupId,
    existingTokenInRegistry,
    prefetchMutation.isPending,
    dataObjectSchemaId,
    modelId,
    territoryId,
    columnFilterKey,
    sortColumnUuid,
    sortAscending,
    page,
    limit,
    isFetchingNewToken,
    tokenKey,
    getPrefetchAttempts,
    incrementPrefetchAttempts,
    showDebouncedErrorToast,
    clearDebounceKey,
    prefetchMutation,
    params,
    isPrefetchActive,
  ]);

  // Clear token when queries are invalidated (e.g., after mutations)
  useEffect(() => {
    if (!modelId) {
      return undefined;
    }

    const queryCache = queryClient.getQueryCache();

    return queryCache.subscribe((event) => {
      if (!event || event.type !== 'updated' || !event.query.state.isInvalidated) {
        return;
      }

      const queryKey = event.query.queryKey as string[];

      // Only clear token if query matches ['hierarchy-models', modelId, dataObjectSchemaId, data-field-values, ...]
      if (
        Array.isArray(queryKey) &&
        queryKey.length >= 4 &&
        queryKey[0] === 'hierarchy-models' &&
        queryKey[1] === modelId &&
        queryKey[2] === dataObjectSchemaId &&
        queryKey[3] === 'data-field-values' &&
        isPrefetchActive
      ) {
        clearAllTokensForModel(modelId);
        // TODO: do some checks before clearing spm sheets. not all users would need this.
        clearSPMPrefetchTokenRegistry();
        clearDebounceKey(tokenKey);
      }
    });
  }, [
    modelId,
    dataObjectSchemaId,
    queryClient,
    tokenKey,
    clearAllTokensForModel,
    clearSPMPrefetchTokenRegistry,
    isPrefetchActive,
    clearDebounceKey,
  ]);

  return {
    prefetchMutation,
    isPrefetching: prefetchMutation.isPending || isFetchingNewToken,
  };
};
